<template>
  <modal name="injectionReport" :pivot-y="0" :width="700" @opened="onModalOpened" @closed="reset">
    <div class="modal-content modal-content--no-padding">
      <div class="modal-header">
        <h4>Injection report</h4>
      </div>
      <div class="modal-body modal-body--big-padding send-report">
        <ReportInProgress :orderedReport="orderedReport" />
        <div class="send-report__content">
          <label>Format</label>
          <div class="send-report__wrapper-selector">
            <Tabs v-model="tabSelected" :tabs="tabs" class="send-report__selector" />
            <Btn
              type="primary"
              borderRadius="sm"
              :isLoading="isLoading"
              class="send-report__btn-download send-report__btn-download--desktop"
              @click="download"
            >
              {{ tabSelected.code === 'pdf' ? 'Generate Report' : 'Download' }}
            </Btn>
          </div>

          <Btn
            type="primary"
            borderRadius="sm"
            :isLoading="isLoading"
            class="send-report__btn-download send-report__btn-download--adaptive"
            @click="download"
          >
            {{ tabSelected.code === 'pdf' ? 'Generate Report' : 'Download' }}
          </Btn>

          <Select
            v-if="tabSelected.code === 'pdf'"
            :itemSelected="reportTypeSelected"
            :items="reportTypes"
            labelProp="title"
            :hasSearch="true"
            padding="md"
            placeholderSearch="Choose report type"
            titleBottomSheet="Choose report type"
            title="Report type"
            class="send-report__type"
            @update:itemSelected="selectReportType"
          />

          <div
            v-if="reportTypeSelected.id === 'rawData' && tabSelected.code === 'pdf'"
            class="send-report__information"
          >
            The report will only contain the raw data from the device. <br />
            If you want to see the processed version, select another report type.
          </div>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
  import ModalComponent from 'components/element/ModalComponent.vue';
  import Tabs from '@/uikitProject/selectors/Tabs';
  import Btn from '@/uikitBase/btns/Btn';
  import { downloadFile } from 'utils/fileHelpers.ts';
  import { AuthService } from '@/services/auth.service.ts';
  import { InjectionCsv } from 'api/injection-csv';
  import _ from 'lodash';
  import Select from '@/uikitBase/selects/vueSelect/Select.vue';
  import { apiReports } from '@/api/graphql/cloud/reports';
  import ReportInProgress from './ReportInProgress.vue';

  const tabs = [
    { title: 'PDF', code: 'pdf' },
    { title: 'CSV', code: 'csv' },
  ];

  const REPORT_TYPES = [
    { title: 'Print processed data', id: 'processedData' },
    { title: 'Print raw data', id: 'rawData' },
    { title: 'Print all data', id: 'full' },
  ];

  export default {
    name: 'injectionReportModal',

    components: {
      Select,
      Btn,
      Tabs,
      ReportInProgress,
      modal: ModalComponent,
    },

    props: {
      sample: {
        type: Object,
        required: true,
      },
      injection: {
        type: Object,
        required: true,
      },
      method: {
        type: Object,
        required: true,
      },
      column: {
        type: Object,
        required: true,
      },
      postprocessings: {
        type: Array,
        default: () => [],
      },
      isShared: {
        type: Boolean,
      },
      owner: {
        type: Object,
      },
      sampleToken: {
        type: String,
      },
    },

    data: () => ({
      tabs,
      tabSelected: tabs[0],

      isLoading: false,
      reportTypeSelected: REPORT_TYPES[0],
      reportTypes: REPORT_TYPES,
      orderedReport: null,
      subscriptionForReport: null,
    }),

    methods: {
      onModalOpened() {
        this.init();
      },

      reset() {
        this.tabSelected = tabs[0];
        this.subscriptionForReport?.unsubscribe?.();
        this.subscriptionForReport = null;
        this.orderedReport = null;
      },

      init() {
        this.subscriptionForReport?.unsubscribe?.();
        if (this.injection?.id) {
          this.subscriptionForReport = apiReports.subscribeForReportByInjectionId(
            this.injection.id,
            (report) => {
              const item = Array.isArray(report) ? report[0] : report;
              this.orderedReport = item ?? null;
            },
            this.sampleToken,
            this.injection.id,
          );
        }
      },

      async downloadCSV() {
        const { response } = await InjectionCsv.get({
          id: this.injection.id,
          shareToken: this.sampleToken,
        });
        const blob = await response.blob();

        const filename = _.trim(
          response.headers.get('Content-Disposition').split('filename=')[1],
          '"',
        );

        downloadFile(blob, filename);
      },

      async download() {
        if (!this.injection?.id) {
          this.notifyError('Injection data is unavailable!');
          return;
        }

        if (this.tabSelected.code === 'pdf') {
          if (this.orderedReport) {
            this.$modal.show('dialog', {
              title: 'Generate new report?',
              text:
                'A report already exists for this injection. Generating will create a new report and the previous report will be lost.',
              buttons: [
                {
                  title: 'Generate new',
                  default: true,
                  handler: () => {
                    this.requestReportGeneration();
                    this.$modal.hide('dialog');
                  },
                  class: 'vue-dialog-button blue-text',
                },
                {
                  title: 'Cancel',
                  handler: () => {
                    this.$modal.hide('dialog');
                  },
                  class: 'vue-dialog-button red-text',
                },
              ],
            });
          } else {
            await this.requestReportGeneration();
          }
          return;
        }

        if (this.tabSelected.code === 'csv') {
          try {
            this.isLoading = true;
            await this.downloadCSV();
          } catch (e) {
            this.notifyError('Unable to download a report!');
          } finally {
            this.isLoading = false;
          }
          return;
        }

        this.notifyError('This report type is not supported!');
      },

      async requestReportGeneration() {
        try {
          this.isLoading = true;

          const token = this.sampleToken
            ? await AuthService.getHasuraAnonymousToken(
                this.sampleToken,
                String(this.injection.id),
                true,
              )
            : await AuthService.refreshAccessToken();

          if (!token) {
            this.notifyError('Authentication expired. Please sign in again.');
            return;
          }

          const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const locale = navigator.language || navigator.userLanguage || 'en-US';

          await apiReports.createReport(
            'injection',
            {
              reportType: this.reportTypeSelected.id,
              timeZone,
              locale,
            },
            token,
            null,
            this.injection.id,
            this.sampleToken,
            this.injection.id,
          );
        } catch (e) {
          console.warn(e);
          this.notifyError('Unable to create a report!');
        } finally {
          this.isLoading = false;
        }
      },

      selectReportType(reportType) {
        this.reportTypeSelected = reportType;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .send-report {
    position: relative;

    @media (max-width: $screen-xxs-max) {
      padding: 16px;
    }

    &__wrapper-loader {
      z-index: 1;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(255, 255, 255, 0.7);
    }

    &__loader {
      margin: 0;
    }

    &__wrapper-selector {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 24px;

      @media (max-width: $screen-xxs-max) {
        margin-bottom: 16px;
      }
    }

    &__btn-download,
    &__btn-send {
      font-weight: $weight-bold;
    }

    &__btn-download {
      &--adaptive {
        display: none;

        @media (max-width: $screen-xxs-max) {
          display: block;
          margin-bottom: 24px;
          margin-left: auto;
        }
      }

      &--desktop {
        display: block;

        @media (max-width: $screen-xxs-max) {
          display: none;
        }
      }
    }

    &__type {
      margin-bottom: 20px;
    }

    &__selector {
      margin-right: 10px;

      @media (max-width: 440px) {
        width: 100%;
        margin-right: 0;
      }
    }

    &__information {
      width: 100%;
      border-radius: $border-radius__md;
      background-color: $color-bg-information;
      padding: 10px;
      margin-bottom: 20px;

      @media (max-width: $screen-xxs-max) {
        padding: 16px;
      }
    }
  }
</style>
